import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Autocomplete from "@mui/material/Autocomplete";
import { CircularProgress, TextField } from "@mui/material";
import { CheckToken } from "../login/AccessToken";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
const DEBOUNCE_MS = 300;
const CUSTOM_OPTION = { name: "Custom", isCustom: true };

function doctorName(doctor) {
  return String(doctor?.name || "").trim();
}

function uniqueDoctorsByName(list) {
  const seen = new Set();
  const doctors = [];
  (Array.isArray(list) ? list : []).forEach((doctor) => {
    const name = doctorName(doctor);
    const key = name.toLowerCase();
    if (!name || seen.has(key)) return;
    seen.add(key);
    doctors.push({ name, doctor_id: doctor.doctor_id || doctor.dr_id || "" });
  });
  return doctors;
}

async function fetchFacilityDoctors(token) {
  const headers = { Authorization: `Bearer ${token}` };
  const endpoints = [
    "get_doctors_based_on_facility_user/",
    "get_all_doctors/?scope=facility",
  ];

  for (const endpoint of endpoints) {
    try {
      const res = await axios.get(`${REACT_APP_BACKEND_ROUTE_URL}${endpoint}`, {
        headers,
      });
      if (res?.data?.status !== "Success") continue;
      const list = res.data?.data ?? res.data?.doctor_list ?? [];
      if (list.length) return uniqueDoctorsByName(list);
    } catch (err) {
      console.warn(`Doctor list endpoint failed: ${endpoint}`, err);
    }
  }

  return [];
}

export default function ReferredByDoctorField({ value, onChange }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const token = sessionStorage.getItem("accessToken") || "";
    if (!token) return undefined;

    CheckToken();
    setLoading(true);
    fetchFacilityDoctors(token)
      .then((list) => {
        if (!cancelled) setDoctors(list);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const options = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    const list = q
      ? doctors.filter((doctor) => doctor.name.toLowerCase().includes(q))
      : doctors;
    const typed = inputValue.trim();
    const hasExact = doctors.some(
      (doctor) => doctor.name.toLowerCase() === typed.toLowerCase(),
    );
    const extra =
      typed && !hasExact && typed.toLowerCase() !== "custom"
        ? [{ name: typed, isCustom: true }]
        : [];
    return [...list, ...extra, CUSTOM_OPTION];
  }, [doctors, debouncedQuery, inputValue]);

  const selected = useMemo(() => {
    if (!value) return null;
    return (
      doctors.find((doctor) => doctor.name === value) || {
        name: value,
        isCustom: true,
      }
    );
  }, [value, doctors]);

  const commitName = (name) => {
    const trimmed = String(name || "").trim();
    onChange(trimmed.toLowerCase() === "custom" ? "" : trimmed);
  };

  return (
    <Autocomplete
      id="referredBy"
      size="small"
      fullWidth
      freeSolo
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option?.name || ""
      }
      isOptionEqualToValue={(option, optionValue) => {
        if (typeof option === "string" || typeof optionValue === "string") {
          return String(option?.name || option) === String(optionValue?.name || optionValue);
        }
        if (option?.isCustom && optionValue?.isCustom) {
          return option.name === optionValue.name;
        }
        return option?.name === optionValue?.name;
      }}
      filterOptions={(opts) => opts}
      noOptionsText={loading ? "Loading doctors..." : "No doctors found"}
      onChange={(_, option) => {
        if (!option) {
          commitName("");
          setInputValue("");
          setDebouncedQuery("");
          return;
        }
        if (typeof option === "string") {
          commitName(option);
          setInputValue(option);
          return;
        }
        if (option.isCustom && option.name === "Custom") {
          commitName("");
          setInputValue("");
          return;
        }
        commitName(option.name);
        setInputValue(option.name);
      }}
      onInputChange={(_, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === "clear") {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          setDebouncedQuery("");
          commitName("");
          return;
        }
        if (reason !== "input") return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          setDebouncedQuery(newInputValue);
          debounceRef.current = null;
        }, DEBOUNCE_MS);
      }}
      onBlur={() => {
        if (inputValue.trim() && inputValue.trim() !== value) {
          commitName(inputValue);
        }
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.isCustom ? `custom-${option.name}` : option.name}>
          {option.isCustom && option.name === "Custom"
            ? "Custom"
            : option.isCustom
              ? `Use “${option.name}”`
              : option.name}
        </li>
      )}
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={loading ? "Loading doctors..." : "Select doctor or Custom"}
          inputProps={{
            ...params.inputProps,
            name: "referredBy",
            autoComplete: "off",
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              minHeight: "38px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ced4da",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#125386",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#125386",
                borderWidth: "1.5px",
              },
            },
          }}
        />
      )}
    />
  );
}
